import { Injectable, inject } from '@angular/core';
import { ApiService } from '@/apiConnection/ApiService';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Urls } from '@/apiConnection/urls';
import { ConnectivityService } from '@/offline/connectivity.service';

const STORAGE_KEY = 'documentDeviceIdentity';
const HEARTBEAT_INTERVAL_MS = 20000;

export interface DocumentDeviceIdentity {
    name: string;
}

/**
 * Manages this browser's identity as the company's designated offline
 * document-generation fallback device. Deliberately backed by NOTHING on the
 * server - no table, no model, no per-device id: `documentDeviceIdentity` in
 * this browser's own localStorage is the entire source of truth for "am I the
 * designated one", exactly as originally specified. The only server
 * involvement is a stateless `POST document-device/heartbeat` that just
 * triggers a public-channel broadcast (see DocumentDeviceMonitorService) so
 * other browsers can see this one is still alive - nothing is persisted.
 */
@Injectable({ providedIn: 'root' })
export class DocumentDeviceService {

    private apiService = inject(ApiService);
    private connectivity = inject(ConnectivityService);
    private authenticationService = inject(AuthenticationService);

    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

    constructor() {
        if (this.readIdentity()) {
            this.startHeartbeat();
        }
    }

    readIdentity(): DocumentDeviceIdentity | null {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;
        try {
            return JSON.parse(stored);
        } catch {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
    }

    /** Designates THIS browser locally - no server round-trip beyond the heartbeat that immediately follows. */
    designate(name: string): void {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ name } satisfies DocumentDeviceIdentity));
        this.startHeartbeat();
    }

    /** Un-designates this browser locally. Does NOT (can't - there's no server record) affect any other browser that has designated itself. */
    forget(): void {
        this.stopHeartbeat();
        localStorage.removeItem(STORAGE_KEY);
    }

    private startHeartbeat(): void {
        if (this.heartbeatTimer) return;
        this.heartbeat();
        this.heartbeatTimer = setInterval(() => this.heartbeat(), HEARTBEAT_INTERVAL_MS);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private heartbeat(): void {
        if (!this.connectivity.backendOnline()) return;
        const identity = this.readIdentity();
        if (!identity) return;
        const companyId = this.authenticationService.effectiveCompanyId;
        if (!companyId) return;

        // Fire-and-forget: a failure here just means this tick's broadcast didn't go
        // out, nothing to roll back or reconcile since nothing was persisted.
        this.apiService.post(Urls.urls.documentDeviceHeartbeat, { name: identity.name }).subscribe();
    }
}
